import { supabase } from './supabase-client.js';
import type { Session, User, AuthError } from '@supabase/supabase-js';

const MANAGER_SESSION_KEY = 'pwd_manager_session';
const MANAGER_OAUTH_PENDING_KEY = 'pwd_manager_oauth_pending';
const ALLOWED_MANAGER_DOMAIN = '@prestigeworlddrive.com';

interface ManagerSessionValidation {
  valid: boolean;
  reason:
    | 'valid'
    | 'no_supabase_session'
    | 'invalid_domain'
    | 'missing_employee'
    | 'missing_local_token'
    | 'token_mismatch'
    | 'query_error';
  session: Session | null;
  user: User | null;
  email: string | null;
  sessionToken: string | null;
}

export async function getSession(): Promise<{ session: Session | null; error: AuthError | Error | null }> {
  try {
    const { data, error } = await supabase.auth.getSession();
    return { session: data.session || null, error: error || null };
  } catch (error) {
    return { session: null, error: error as Error };
  }
}

export async function getCurrentUser(): Promise<{ user: User | null; error: AuthError | Error | null }> {
  try {
    const { data, error } = await supabase.auth.getUser();
    return { user: data.user || null, error: error || null };
  } catch (error) {
    return { user: null, error: error as Error };
  }
}

export function getManagerSessionToken(): string | null {
  try {
    return window.localStorage.getItem(MANAGER_SESSION_KEY);
  } catch {
    return null;
  }
}

export function hasManagerSessionToken(): boolean {
  return !!getManagerSessionToken();
}

export function clearManagerSessionToken(): void {
  try {
    window.localStorage.removeItem(MANAGER_SESSION_KEY);
  } catch {
    /* ignore storage access errors */
  }
}

export function setManagerSessionToken(token: string): void {
  window.localStorage.setItem(MANAGER_SESSION_KEY, token);
}

export function markManagerOAuthPending(): void {
  try {
    window.sessionStorage.setItem(MANAGER_OAUTH_PENDING_KEY, '1');
  } catch {
    /* ignore storage access errors */
  }
}

export function hasManagerOAuthPending(): boolean {
  try {
    return window.sessionStorage.getItem(MANAGER_OAUTH_PENDING_KEY) === '1';
  } catch {
    return false;
  }
}

export function clearManagerOAuthPending(): void {
  try {
    window.sessionStorage.removeItem(MANAGER_OAUTH_PENDING_KEY);
  } catch {
    /* ignore storage access errors */
  }
}

export async function clearManagerPrivilegedState(options: { signOutSupabase?: boolean } = {}): Promise<void> {
  clearManagerSessionToken();
  clearManagerOAuthPending();
  if (options.signOutSupabase) {
    try {
      await supabase.auth.signOut({ scope: 'local' });
    } catch {
      /* already signed out or storage unavailable */
    }
  }
}

/**
 * Privileged manager entry requires BOTH:
 * 1. an existing Supabase identity session for the current browser
 * 2. a browser-local pwd_manager_session token that matches the current
 *    employees.manager_session_token for that same manager account
 */
export async function validateManagerPrivilegedSession(): Promise<ManagerSessionValidation> {
  const { data, error } = await supabase.auth.getSession();
  if (error) {
    return {
      valid: false,
      reason: 'no_supabase_session',
      session: null,
      user: null,
      email: null,
      sessionToken: null,
    };
  }

  const session = data.session || null;
  const user = session?.user || null;
  const email = user?.email || null;
  if (!session || !user || !email) {
    return { valid: false, reason: 'no_supabase_session', session, user, email, sessionToken: null };
  }

  if (!email.endsWith(ALLOWED_MANAGER_DOMAIN)) {
    return { valid: false, reason: 'invalid_domain', session, user, email, sessionToken: null };
  }

  const localToken = getManagerSessionToken();
  if (!localToken) {
    return { valid: false, reason: 'missing_local_token', session, user, email, sessionToken: null };
  }

  const { data: employee, error: employeeError } = await supabase
    .from('employees')
    .select('manager_session_token')
    .eq('email', email)
    .maybeSingle();

  if (employeeError) {
    return { valid: false, reason: 'query_error', session, user, email, sessionToken: null };
  }
  if (!employee) {
    return { valid: false, reason: 'missing_employee', session, user, email, sessionToken: null };
  }
  if (employee.manager_session_token !== localToken) {
    return { valid: false, reason: 'token_mismatch', session, user, email, sessionToken: null };
  }

  return { valid: true, reason: 'valid', session, user, email, sessionToken: localToken };
}

export function subscribeManagerSession(callback: (token: string | null) => void): () => void {
  function handleStorage(event: StorageEvent) {
    if (event.key === MANAGER_SESSION_KEY) {
      callback(getManagerSessionToken());
    }
  }

  window.addEventListener('storage', handleStorage);
  return () => window.removeEventListener('storage', handleStorage);
}

export function getManagerEntryUrl(): string {
  return '/portal/manager.html';
}
